import type { AxiosError } from "axios";
import { ApiErrorType, HttpStatusCode, type ApiError } from "./types";

export function getErrorTypeFromStatus(status?: number): ApiErrorType {
  if (!status) {
    return ApiErrorType.NETWORK_ERROR;
  }

  switch (status) {
    case HttpStatusCode.UNAUTHORIZED:
      return ApiErrorType.UNAUTHORIZED;
    case HttpStatusCode.FORBIDDEN:
      return ApiErrorType.FORBIDDEN;
    case HttpStatusCode.NOT_FOUND:
      return ApiErrorType.NOT_FOUND;
    case HttpStatusCode.BAD_REQUEST:
    case HttpStatusCode.UNPROCESSABLE_ENTITY:
      return ApiErrorType.VALIDATION_ERROR;
    case HttpStatusCode.TOO_MANY_REQUESTS:
      return ApiErrorType.RATE_LIMITED;
    case HttpStatusCode.INTERNAL_SERVER_ERROR:
    case HttpStatusCode.BAD_GATEWAY:
    case HttpStatusCode.SERVICE_UNAVAILABLE:
    case HttpStatusCode.GATEWAY_TIMEOUT:
      return ApiErrorType.SERVER_ERROR;
    default:
      if (status >= 400 && status < 500) {
        return ApiErrorType.VALIDATION_ERROR;
      }
      if (status >= 500) {
        return ApiErrorType.SERVER_ERROR;
      }
      return ApiErrorType.UNKNOWN_ERROR;
  }
}

export function extractErrorMessage(error: AxiosError): string {
  if (!error.response) {
    if (error.code === "ECONNABORTED") {
      return "Request timeout. Please try again.";
    }
    if (error.message === "Network Error") {
      return "Network error. Please check your internet connection.";
    }
    return error.message || "An unexpected error occurred.";
  }

  const data = error.response.data as Record<string, unknown> | undefined;
  if (data) {
    if (typeof data.message === "string") {
      return data.message;
    }
    if (typeof data.error === "string") {
      return data.error;
    }
    if (typeof data.error === "object" && data.error !== null) {
      const errorObj = data.error as Record<string, unknown>;
      if (typeof errorObj.message === "string") {
        return errorObj.message;
      }
    }
    if (Array.isArray(data.errors)) {
      return data.errors
        .map((e) =>
          typeof e === "string"
            ? e
            : (e as Record<string, unknown>)?.message || String(e),
        )
        .join(", ");
    }
    if (typeof data.detail === "string") {
      return data.detail;
    }
  }

  return getDefaultErrorMessage(error.response.status);
}

export function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case HttpStatusCode.BAD_REQUEST:
      return "Invalid request. Please check your input.";
    case HttpStatusCode.UNAUTHORIZED:
      return "Session expired. Please log in again.";
    case HttpStatusCode.FORBIDDEN:
      return "You do not have permission to perform this action.";
    case HttpStatusCode.NOT_FOUND:
      return "The requested resource was not found.";
    case HttpStatusCode.CONFLICT:
      return "This resource already exists or conflicts with another.";
    case HttpStatusCode.UNPROCESSABLE_ENTITY:
      return "The submitted data is invalid.";
    case HttpStatusCode.TOO_MANY_REQUESTS:
      return "Too many requests. Please wait a moment and try again.";
    case HttpStatusCode.INTERNAL_SERVER_ERROR:
      return "Server error. Please try again later.";
    case HttpStatusCode.BAD_GATEWAY:
    case HttpStatusCode.SERVICE_UNAVAILABLE:
    case HttpStatusCode.GATEWAY_TIMEOUT:
      return "Service temporarily unavailable. Please try again later.";
    default:
      return "An unexpected error occurred.";
  }
}

export function createApiError(error: AxiosError): ApiError {
  const status = error.response?.status;
  const data = error.response?.data as Record<string, unknown> | undefined;

  if (error.code === "ERR_CANCELED" || error.message === "canceled") {
    return {
      type: ApiErrorType.CANCELLED,
      message: "Request was cancelled.",
      originalError: error,
    };
  }

  if (error.code === "ECONNABORTED") {
    return {
      type: ApiErrorType.TIMEOUT_ERROR,
      message: "Request timeout. Please try again.",
      originalError: error,
    };
  }

  return {
    type: getErrorTypeFromStatus(status),
    status,
    message: extractErrorMessage(error),
    code: data?.code as string | undefined,
    details: data?.details as Record<string, unknown> | undefined,
    originalError: error,
  };
}

export function isRetryableError(
  error: AxiosError,
  retryStatusCodes: number[],
): boolean {
  if (!error.response) {
    return error.code !== "ERR_CANCELED";
  }
  return retryStatusCodes.includes(error.response.status);
}
